import React, { useState } from "react";
import { Heart } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Small like/unlike control for comments. Optimistic UI + rollback on error.
 * Backend: POST/DELETE /comment-likes with body/params
 *   { target_kind: "report_message" | "event_comment", target_id }.
 * Duplicate posts are safe: backend returns created=false and current count.
 */
export function CommentLikeButton({ targetKind, targetId, initialCount = 0, initialLiked = false, testid }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [count, setCount] = useState(Number(initialCount) || 0);
  const [liked, setLiked] = useState(!!initialLiked);
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    if (!user) { toast.info(t("signin_required") || "Accedi per continuare"); return; }
    if (busy) return;
    setBusy(true);
    const prevLiked = liked;
    const prevCount = count;
    // Optimistic
    setLiked(!prevLiked);
    setCount(prevCount + (prevLiked ? -1 : 1));
    try {
      if (prevLiked) {
        const { data } = await api.delete("/comment-likes", { params: { target_kind: targetKind, target_id: targetId } });
        setCount(data.like_count);
        setLiked(false);
      } else {
        const { data } = await api.post("/comment-likes", { target_kind: targetKind, target_id: targetId });
        setCount(data.like_count);
        setLiked(true);
      }
    } catch (err) {
      // Rollback on failure
      setLiked(prevLiked);
      setCount(prevCount);
      toast.error(err.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      data-testid={testid || `comment-like-${targetId}`}
      onClick={toggle}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs transition-colors ${liked ? "text-red-600 bg-red-50" : "text-slate-500 hover:text-red-600 hover:bg-red-50"} ${busy ? "opacity-70" : ""}`}
      aria-pressed={liked}
      aria-label={liked ? t("comment_unlike") : t("comment_like")}
    >
      <Heart size={13} fill={liked ? "currentColor" : "none"} />
      {count > 0 && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

/**
 * Segmented sort picker used in comment lists. Value is one of:
 *   "relevant" | "liked" | "recent" | "oldest".
 */
export function CommentSortSelect({ value, onChange, testid }) {
  const { t } = useI18n();
  const options = [
    { k: "relevant", label: t("comment_sort_relevant") },
    { k: "liked",    label: t("comment_sort_liked") },
    { k: "recent",   label: t("comment_sort_recent") },
    { k: "oldest",   label: t("comment_sort_oldest") },
  ];
  return (
    <div className="inline-flex flex-wrap gap-1" data-testid={testid || "comment-sort"}>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold self-center mr-1">
        {t("comment_sort_label")}
      </span>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          data-testid={`comment-sort-${o.k}`}
          onClick={() => onChange(o.k)}
          className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${value === o.k ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
